import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfitSettings = ({ token }) => {
  const [green, setGreen] = useState('40');
  const [amber, setAmber] = useState('20');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/settings/profit`, { headers });
        setGreen(String(res.data.green_min));
        setAmber(String(res.data.amber_min));
      } catch (err) {
        // keep defaults
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings/profit`, {
        green_min: parseFloat(green),
        amber_min: parseFloat(amber),
      }, { headers });
      setGreen(String(res.data.green_min));
      setAmber(String(res.data.amber_min));
      toast.success('Profit thresholds saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save those thresholds');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <Card className="bg-white max-w-2xl" data-testid="profit-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="w-5 h-5 text-green-600" /> Profit colours
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Green from (%)</label>
            <Input type="number" step="1" min="0" max="100" value={green} onChange={(e) => setGreen(e.target.value)} data-testid="profit-green-input" />
            <p className="text-xs text-slate-500 mt-1">Default 40%</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Amber from (%)</label>
            <Input type="number" step="1" min="0" max="100" value={amber} onChange={(e) => setAmber(e.target.value)} data-testid="profit-amber-input" />
            <p className="text-xs text-slate-500 mt-1">Default 20%. Anything below shows red</p>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600" data-testid="profit-settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save profit colours
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProfitSettings;
